import React from "react";
import { cn } from "@/lib/utils";
import { StatusPill, type VbStatus } from "@/components/vb";
import { providerMeta, KNOWN_PROVIDERS } from "./providerMeta";
import type { RunLeg, RunLegStatus } from "./runProgress";

/** Map a per-provider leg status to the shadow status the StatusPill renders.
 *  Jarts calls a not-yet-started leg "QUEUED"; the pill's PENDING label is
 *  "Queued", so they line up. */
function pillStatus(s: RunLegStatus): VbStatus {
  return s === "QUEUED" ? "PENDING" : s;
}

/** One provider chip: brand logo (pulses while the leg is active), the provider
 *  brand name, and a live questions-answered count + status. */
function ProviderChip({ leg }: { leg: RunLeg }) {
  const meta = providerMeta(leg.provider);
  const active = leg.status === "QUEUED" || leg.status === "IN_PROGRESS";
  return (
    <div className={cn("vb-run-model", meta.className, active && "is-active")}>
      <span className="logo">{meta.logo}</span>
      <div className="vb-run-model__meta">
        <span className="vb-run-model__name">{meta.label}</span>
        <span className="vb-run-model__stat">
          {leg.total > 0 && (
            <span className="vb-run-model__count" aria-label={`${leg.done} of ${leg.total} answered`}>
              {leg.done}/{leg.total}
            </span>
          )}
          <StatusPill status={pillStatus(leg.status)} />
        </span>
      </div>
    </div>
  );
}

/** The provider row. Jarts seeds `payload.runs` with the three providers
 *  (QUEUED) synchronously at trigger and merges live done/total into it, so
 *  `legs` is populated from the first poll on. The placeholder below only
 *  guards the rare edge where the best-effort seed didn't land yet (the worker's
 *  runs_init repopulates within a second), so the card never renders zero chips. */
export function RunProviders({ legs }: { legs: RunLeg[] }) {
  const rows: RunLeg[] =
    legs.length > 0
      ? legs
      : KNOWN_PROVIDERS.map((p) => ({
          key: p,
          provider: p,
          model: providerMeta(p).label,
          status: "QUEUED" as RunLegStatus,
          done: 0,
          total: 0,
        }));
  return (
    <div className="vb-run-models">
      {rows.map((leg) => (
        // Key on provider (stable) not leg.key: the placeholder keys by provider
        // alone while real legs key by provider:model, so keying on leg.key would
        // remount every chip on the first real poll and restart the pulse.
        <ProviderChip key={leg.provider} leg={leg} />
      ))}
    </div>
  );
}

export default RunProviders;
